import "./site/error-beacon";
import { Sigil, SigilSample } from "./games/sigil/sigil";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.sigil_pb";
const HANDLE_KEY = "aether.handle";

// Retry any submits that failed on a prior page load (transient 5xx / network).
flushFailedSubmits("sigil");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startSigilRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/sigil", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

/**
 * v5(b) — "one [tier] short of ritual · glyph N" tag for game-over.
 *
 * Per docs/dossier-games/04-sigil/07-telemetry.md §6.3. Ritual requires
 * every glyph reach gold or better; when the player got exactly one glyph
 * short of that bar, surface which glyph and what tier they hit, so they
 * know what specifically prevented ritual instead of staring at the score
 * and counting tier pips manually. Silent on:
 *   - completed rituals (already covered by "RITUAL COMPLETE +3000")
 *   - runs more than one glyph short (the gap is too wide to be motivating)
 *   - runs with any null tiers (incomplete data — don't fabricate a story)
 */
function formatAlmostRitualTag(perGlyphTier: (string | null)[]): string | null {
  if (perGlyphTier.length !== 5) return null;
  if (perGlyphTier.some((t) => t === null)) return null;
  const goldRanks = new Set(["gold", "platinum"]);
  const shortIdxs: number[] = [];
  for (let i = 0; i < perGlyphTier.length; i++) {
    if (!goldRanks.has(perGlyphTier[i]!)) shortIdxs.push(i);
  }
  if (shortIdxs.length !== 1) return null;
  const idx = shortIdxs[0];
  return `one ${perGlyphTier[idx]} short of ritual · glyph ${idx + 1}`;
}

async function finishSigilRun(runId: string, handle: string, samples: SigilSample[], coarse: boolean, pressureMode: boolean) {
  const url = `/api/runs/sigil/${runId}`;
  const body = { handle, samples, coarse, pressure_mode: pressureMode };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("sigil", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("sigil", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudGlyph = document.getElementById("hud-glyph")!;
const hudLast = document.getElementById("hud-last")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const btnReplay = document.getElementById("btn-replay") as HTMLButtonElement | null;
const pressureCheckbox = document.getElementById("pressure-mode-checkbox") as HTMLInputElement | null;
// Persist the opt-in flag across sessions so a player who turned it on
// once doesn't have to re-tick it every run.
const PRESSURE_KEY = "aether.sigil_pressure_mode";
try {
  if (pressureCheckbox && localStorage.getItem(PRESSURE_KEY) === "1") pressureCheckbox.checked = true;
} catch { /* private mode */ }
pressureCheckbox?.addEventListener("change", () => {
  try { localStorage.setItem(PRESSURE_KEY, pressureCheckbox.checked ? "1" : "0"); } catch { /* private */ }
});

let game: Sigil | null = null;
let activeRunId: string | null = null;
let pendingSamples: SigilSample[] | null = null;
let pendingCoarse = false;
let pendingPressureMode = false;
// v5 — last-run cache for ghost-replay. Held in memory only; cleared on
// reload. Replay reuses the same seed so the glyph batch matches exactly.
let lastRun: { samples: SigilSample[]; seed: number; coarse: boolean; msgText: string } | null = null;

async function startRun() {
  if (game) game.dispose();
  if (btnReplay) btnReplay.hidden = true;
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startSigilRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  // Frozen at run-start — flipping mid-run would invalidate the on-wire
  // pressure semantics (some samples with p, some without).
  const pressureMode = pressureCheckbox?.checked === true;

  game = new Sigil(canvas, {
    onHud: (gIdx, lastBucket, score) => {
      hudGlyph.textContent = `${gIdx + 1} / 5`;
      hudLast.textContent = lastBucket;
      hudScore.textContent = score.toLocaleString();
    },
    onGameOver: async ({ samples, scoreEstimate, bestCombo, bucketCounts, coarse, pressureMode: pm }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      const summary = `${bucketCounts.tight}● · ${bucketCounts.close}○ · ${bucketCounts.lost}✕`;
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${summary} · best combo ${bestCombo}`;
      btnStart.textContent = "Try again";
      // Cache for ghost-replay. Only live runs are cacheable (replay
      // doesn't reach this branch — its finish path calls onReplayEnd).
      lastRun = { samples, seed, coarse, msgText: msgBody.textContent };
      if (btnReplay) btnReplay.hidden = false;
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "sigil", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("sigil", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishSigilRun(activeRunId, handle, samples, coarse, pm);
        if (res) {
          const tags: string[] = [`ranked #${res.rank} of ${res.total}`];
          if (res.ritual_completed) tags.unshift("RITUAL COMPLETE +3000");
          if (typeof res.mastery_bonus === "number" && res.mastery_bonus > 0) tags.push(`mastery +${res.mastery_bonus}`);
          if (pm) tags.push("✎ pressure");
          if (!res.ritual_completed && Array.isArray(res.per_glyph_tier)) {
            const almost = formatAlmostRitualTag(res.per_glyph_tier);
            if (almost) tags.push(almost);
          }
          msgBody.textContent += ` · ${tags.join(" · ")}`;
        }
      } else {
        pendingSamples = samples;
        pendingCoarse = coarse;
        pendingPressureMode = pm;
        submitForm.hidden = false;
      }
    },
  }, seed, { pressureMode });
  game.armAudio();

  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Sigil";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

async function startReplay() {
  if (!lastRun) return;
  if (game) game.dispose();
  activeRunId = null; // never submit during replay
  if (btnReplay) btnReplay.hidden = true;
  const { samples, seed, coarse, msgText } = lastRun;
  game = new Sigil(canvas, {
    onHud: (gIdx, lastBucket) => {
      hudGlyph.textContent = `${gIdx + 1} / 5`;
      hudLast.textContent = lastBucket;
    },
    onGameOver: () => { /* unreachable in ghost mode */ },
    onReplayEnd: () => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Replay complete";
      msgBody.textContent = msgText;
      btnStart.textContent = "Try again";
      if (btnReplay) btnReplay.hidden = false;
        },
  }, seed, { ghost: { samples, coarse } });
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Replay";
  msgBody.textContent = "";
}

btnReplay?.addEventListener("click", startReplay);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingSamples) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishSigilRun(activeRunId, value, pendingSamples, pendingCoarse, pendingPressureMode);
  pendingSamples = null;
  submitForm.hidden = true;
  if (res) {
    const tags: string[] = [`ranked #${res.rank} of ${res.total}`];
    if (res.ritual_completed) tags.unshift("RITUAL COMPLETE +3000");
    if (typeof res.mastery_bonus === "number" && res.mastery_bonus > 0) tags.push(`mastery +${res.mastery_bonus}`);
    if (pendingPressureMode) tags.push("✎ pressure");
    if (!res.ritual_completed && Array.isArray(res.per_glyph_tier)) {
      const almost = formatAlmostRitualTag(res.per_glyph_tier);
      if (almost) tags.push(almost);
    }
    msgBody.textContent += ` · ${tags.join(" · ")}`;
  }
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
